import { useState, useEffect } from "react";
import FaqIcon from "./FaqIcon";
import { ContentContainer } from "components/Container";
import { FAQIconType } from "types";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

interface FAQItem {
  question: string;
  answer: string;
}

const stripHtml = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqContent = t("faq.questions", { returnObjects: true }) as FAQItem[];

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": Object.values(faqContent).map((item: FAQItem) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": stripHtml(item.answer)
        }
      }))
    };

    const existingScript = document.getElementById("faq-schema");
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.id = "faq-schema";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById("faq-schema");
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqContent]);

  return (
    <section id="faq" className="mt-20 lg:mt-36 scroll-mt-10">
      <ContentContainer>
        <>
          <h2 className="max-w-78 md:max-w-full title-text-style">
            {t("faq.title")}
          </h2>

          <p className="subtitle-text-style max-w-812px mt-5 mb-8">
            {t("faq.subtitle")}
          </p>

          {Object.values(faqContent).map((item: FAQItem, index: number) => (
            <div
              key={item.question}
              className="border-b border-borderPrimary py-8 last:border-0"
            >
              <h3 className="text-2xl lg:text-[26px] font-medium text-textPrimary">
                <button
                  type="button"
                  className="flex w-full justify-between items-center text-left"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span>{item.question}</span>
                  <span className="ml-4">
                    {openIndex === index ? (
                      <FaqIcon type={FAQIconType.Minus} />
                    ) : (
                      <FaqIcon type={FAQIconType.Plus} />
                    )}
                  </span>
                </button>
              </h3>
              {/* Always rendered (hidden when closed) so the answers are in the prerendered HTML for search engines */}
              <div
                id={`faq-answer-${index}`}
                className="mt-4 subtitle-text-style w-11/12"
                hidden={openIndex !== index}
              >
                {parse(item.answer)}
              </div>
            </div>
          ))}
        </>
      </ContentContainer>
    </section>
  );
};
